import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Building2, Plus, Search, List, Settings2,
  Globe, Users, Layers, RefreshCw,
} from 'lucide-react'
import {
  useLaboratoriosDashboard,
  usePaises,
  useTiposLaboratorioFull,
} from '../hooks/useBdClientes'
import { StatsCard } from '../components/StatsCard'
import type { FiltrosLaboratorios, ChartItem } from '../types'

// Paleta teal: oscuro → claro (modo claro), claro → oscuro (modo oscuro)
const TEAL_LIGHT = [
  '#134e4a', '#115e59', '#0f766e', '#0d9488', '#14b8a6',
  '#2dd4bf', '#5eead4', '#99f6e4', '#ccfbf1', '#f0fdfa',
]
const TEAL_DARK = [
  '#ccfbf1', '#99f6e4', '#5eead4', '#2dd4bf', '#14b8a6',
  '#0d9488', '#0f766e', '#115e59', '#134e4a', '#0c3c38',
]

function getBarColor(index: number, total: number, isDark: boolean): string {
  const palette = isDark ? TEAL_DARK : TEAL_LIGHT
  const mappedIndex = Math.round((index / Math.max(total - 1, 1)) * (palette.length - 1))
  return palette[Math.min(mappedIndex, palette.length - 1)]
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'))
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

function BarChartCard({
  title,
  icon: Icon,
  data,
}: {
  title: string
  icon: React.ElementType
  data: ChartItem[]
}) {
  const isDark = useDarkMode()
  const chartConfig = {
    total: { label: 'Laboratorios', color: '#14b8a6' },
  } satisfies ChartConfig

  if (!data || data.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Icon className="h-4 w-4 text-teal-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Icon className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No hay datos</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-teal-500" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[280px] w-full">
          <BarChart
            accessibilityLayer
            data={data}
            layout="vertical"
            margin={{ left: 0, right: 50 }}
          >
            <XAxis type="number" dataKey="total" hide />
            <YAxis
              dataKey="nombre"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={120}
              tickFormatter={(value) =>
                value.length > 15 ? value.slice(0, 15) + '...' : value
              }
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Bar dataKey="total" radius={4}>
              {data.map((_, index) => (
                <Cell key={index} fill={getBarColor(index, data.length, isDark)} />
              ))}
              <LabelList
                dataKey="total"
                position="right"
                offset={8}
                className="fill-foreground"
                fontSize={12}
                formatter={(value: number) => value.toLocaleString()}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function activoBadge(activo: boolean) {
  if (activo) {
    return <Badge variant="outline" className="badge-gradient text-green-700 dark:text-green-500 border-green-500">Activo</Badge>
  }
  return <Badge variant="outline" className="text-gray-500 border-gray-400">Inactivo</Badge>
}

export function LaboratoriosDashboardPage() {
  useDocumentTitle('Laboratorios - Dashboard')
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState('')
  const [filtros, setFiltros] = useState<FiltrosLaboratorios>({})

  const { data: dashboard, isLoading } = useLaboratoriosDashboard(filtros)
  const { data: paises } = usePaises(true)
  const { data: tipos } = useTiposLaboratorioFull(true)

  const handleSearch = () => {
    setFiltros(prev => ({ ...prev, search: searchInput || undefined }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setFiltros(prev => ({
      ...prev,
      [key]: value === 'all' ? undefined : (key === 'activo' ? value === 'true' : Number(value)),
    }))
  }

  const clearFilters = () => {
    setSearchInput('')
    setFiltros({})
  }

  const hasActiveFilters = Object.values(filtros).some(v => v !== undefined)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-6 w-6 text-black dark:text-white" />
          Dashboard de Laboratorios
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/bd-clientes/laboratorios/seguimiento')}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <List className="mr-2 h-4 w-4" />
            Ver Lista
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/bd-clientes/laboratorios/configuracion')}
            className="border-gray-700 text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Configuración
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/bd-clientes/laboratorios/crear')}
            className="bg-teal-800 hover:bg-teal-900 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Laboratorio
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatsCard
            title="Total Laboratorios"
            value={dashboard.stats.total}
            icon={Building2}
            description={`Activos: ${dashboard.stats.activos} | Inactivos: ${dashboard.stats.inactivos}`}
          />
          <StatsCard
            title="Activos"
            value={dashboard.stats.activos}
            icon={Building2}
            description="Laboratorios activos"
          />
          <StatsCard
            title="Países"
            value={dashboard.stats.total_paises}
            icon={Globe}
            description="Con laboratorios"
          />
          <StatsCard
            title="Grupos"
            value={dashboard.stats.total_grupos}
            icon={Users}
            description="Grupos asociados"
          />
        </div>
      )}

      {/* Filtros */}
      <Card className="p-4 border border-gray-300 dark:border-gray-700">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Buscar por razón social, identificación, correo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} title="Limpiar filtros">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Select
              value={filtros.pais_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('pais_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {paises?.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.tipo_id?.toString() || 'all'}
              onValueChange={(v) => handleFilterChange('tipo_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tipo Laboratorio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {tipos?.map((t) => (
                  <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filtros.activo !== undefined ? filtros.activo.toString() : 'all'}
              onValueChange={(v) => handleFilterChange('activo', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="true">Activo</SelectItem>
                <SelectItem value="false">Inactivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Charts */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[350px]" />
          <Skeleton className="h-[350px]" />
        </div>
      ) : dashboard && (
        <div className="grid gap-4 md:grid-cols-2">
          <BarChartCard
            title="Laboratorios por País"
            icon={Globe}
            data={dashboard.charts.por_pais}
          />
          <BarChartCard
            title="Laboratorios por Tipo"
            icon={Layers}
            data={dashboard.charts.por_tipo}
          />
        </div>
      )}

      {/* Laboratorios Recientes */}
      {isLoading ? (
        <Skeleton className="h-[300px]" />
      ) : dashboard && (
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-teal-500" />
              Laboratorios Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard.laboratorios_recientes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                    <TableHead>Razón Social</TableHead>
                    <TableHead>Identificación</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Grupo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboard.laboratorios_recientes.map((lab) => (
                    <TableRow
                      key={lab.id}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]"
                      onClick={() => navigate(`/bd-clientes/laboratorios/${lab.id}`)}
                    >
                      <TableCell className="font-medium text-black dark:text-white">
                        {lab.razon_social}
                      </TableCell>
                      <TableCell>{lab.identificacion}</TableCell>
                      <TableCell>{lab.pais_nombre}</TableCell>
                      <TableCell>{lab.tipo_nombre}</TableCell>
                      <TableCell>{lab.grupo_nombre}</TableCell>
                      <TableCell>{activoBadge(lab.activo)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(lab.fecha_creacion).toLocaleDateString('es-EC')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Building2 className="h-8 w-8 mb-2 opacity-30" />
                <p className="text-sm mb-3">No hay laboratorios registrados</p>
                <Button
                  size="sm"
                  onClick={() => navigate('/bd-clientes/laboratorios/crear')}
                  className="bg-teal-800 hover:bg-teal-900 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Laboratorio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
